// src/components/agencies/ServiceManager.jsx
import React, { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { updateAgencyServices } from '../../store/actions/agencyActions';

const ServiceManager = ({ agencyId, currentServices = [], allServices = [], onClose, onUpdate }) => {
  const dispatch = useDispatch();
  const [selectedServices, setSelectedServices] = useState(
    currentServices.map(s => s.id)
  );
  const [loading, setLoading] = useState(false);

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    modal: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      width: '90%',
      maxWidth: '600px',
      maxHeight: '80vh',
      overflow: 'hidden',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    },
    header: {
      padding: '1.5rem',
      borderBottom: '1px solid #e5e7eb',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    title: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: '#111827',
      margin: 0,
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '1.5rem',
      cursor: 'pointer',
      color: '#9ca3af',
    },
    content: {
      padding: '1.5rem',
      maxHeight: 'calc(80vh - 130px)',
      overflowY: 'auto',
    },
    serviceList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
    },
    serviceItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '0.5rem',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    serviceItemHover: {
      backgroundColor: '#f3f4f6',
    },
    checkbox: {
      width: '1.25rem',
      height: '1.25rem',
      cursor: 'pointer',
    },
    serviceInfo: {
      flex: 1,
    },
    serviceName: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#111827',
    },
    serviceDescription: {
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    footer: {
      padding: '1.5rem',
      borderTop: '1px solid #e5e7eb',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
    },
    button: {
      padding: '0.5rem 1rem',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      border: 'none',
      transition: 'all 0.2s',
    },
    cancelButton: {
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
    },
    cancelButtonHover: {
      backgroundColor: '#f9fafb',
    },
    saveButton: {
      backgroundColor: '#2563eb',
      color: '#ffffff',
    },
    saveButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    saveButtonDisabled: {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed',
    },
  };

  const handleToggleService = (serviceId) => {
    setSelectedServices(prev => {
      if (prev.includes(serviceId)) {
        return prev.filter(id => id !== serviceId);
      } else {
        return [...prev, serviceId];
      }
    });
  };

  const handleSave = useCallback(async () => {
    setLoading(true);
    try {
      await dispatch(updateAgencyServices(agencyId, selectedServices));
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Failed to update services:', error);
    } finally {
      setLoading(false);
    }
  }, [dispatch, agencyId, selectedServices, onUpdate, onClose]);

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>Gérer les services</h3>
          <button style={styles.closeButton} onClick={onClose}>×</button>
        </div>

        <div style={styles.content}>
          <div style={styles.serviceList}>
            {allServices.map((service) => (
              <div
                key={service.id}
                style={styles.serviceItem}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = styles.serviceItemHover.backgroundColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                onClick={() => handleToggleService(service.id)}
              >
                <input
                  type="checkbox"
                  checked={selectedServices.includes(service.id)}
                  onChange={() => {}}
                  style={styles.checkbox}
                />
                <div style={styles.serviceInfo}>
                  <div style={styles.serviceName}>{service.name}</div>
                  {service.description && (
                    <div style={styles.serviceDescription}>{service.description}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={styles.footer}>
          <button
            onClick={onClose}
            style={styles.button}
            css={[styles.cancelButton]}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.cancelButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.cancelButton.backgroundColor;
            }}
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            style={{
              ...styles.button,
              ...styles.saveButton,
              ...(loading ? styles.saveButtonDisabled : {}),
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = styles.saveButtonHover.backgroundColor;
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = styles.saveButton.backgroundColor;
              }
            }}
          >
            {loading ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ServiceManager;